import React, { useEffect, useState, useCallback } from 'react'
import { Card, Table, Button, Spinner, Badge, Dropdown } from 'react-bootstrap'
import {
  useCreateExportMutation,
  useDeleteExportMutation,
  useListExportsQuery,
} from '../../../services/exportApi'

import { Export, ExportType } from '../../../types/export'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import ConfirmDialog from '../../../components/modals/ConfirmDialog'
import { selectSelectedModelId, setSelectedModelId } from '../../../store/slices/modelSlice'
import ExportButton from '../../../components/buttons/ExportButton'
import { useFetchModelsQuery } from '../../../services/modelsApi'
import ExportTypeDialog from '../../../components/modals/ExportTypeDialog'
import ErrorDialog from '../../../components/modals/ErrorDialog'

const StyledCard = styled(Card)`
  background: ${(props) => props.theme.bgLight || '#2a2a2a'};
  color: ${(props) => props.theme.textLight || '#fff'};
  border: none;
  margin-bottom: 1rem;
`

const StyledTable = styled(Table)`
  color: #fff;

  th,
  td {
    border-color: rgba(255, 255, 255, 0.1);
    padding: 1rem;
    vertical-align: middle;
    color: #fff;
  }

  thead th {
    background: ${(props) => props.theme.bgDark || '#1a1a1a'};
    border-bottom: 2px solid rgba(255, 255, 255, 0.1);
    color: #fff;
  }

  tbody {
    tr {
      &:hover {
        background: ${(props) => props.theme.bgDark || '#1a1a1a'};
      }
      td {
        background: transparent;
        color: #fff;
      }
    }
  }

  .badge {
    font-size: 0.85rem;
    padding: 0.5em 0.85em;
    color: #fff;
  }
`

interface ExportsSubViewProps {
  isModal?: boolean
  onClose?: () => void
}

const ExportsSubView: React.FC<ExportsSubViewProps> = ({ isModal = false, onClose }) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const dispatch = useAppDispatch()
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)
  const [showExportTypeDialog, setShowExportTypeDialog] = useState<boolean>(false)
  const [selectedExport, setSelectedExport] = useState<Export | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)

  const { data: modelsData } = useFetchModelsQuery(
    {
      project_id: selectedProjectId || '',
      page: 0,
      limit: 100,
      sort_key: 'created_at',
      sort_val: -1,
    },
    {
      skip: !selectedProjectId,
    }
  )

  const {
    data: exportsData,
    isLoading,
    error: apiError,
  } = useListExportsQuery(
    {
      page: 0,
      limit: 100,
      sort_key: 'created_at',
      sort_val: -1,
    },
    {
      pollingInterval: 60000,
    }
  )

  const showExportItem = useCallback(
    (exportItem: Export): boolean => {
      return (
        exportItem.project?.id === selectedProjectId ||
        modelsData?.models?.some((model) => model.id === exportItem.model?.id) ||
        exportItem.dataset?.id === project?.datasetid
      )
    },
    [selectedProjectId, modelsData?.models, project?.datasetid]
  )

  // Add effect to restore selected model from localStorage
  useEffect(() => {
    const savedModelId = localStorage.getItem('selectedModelId')
    const existsInModelsData = modelsData?.models?.some((model) => model.id === savedModelId)

    if (savedModelId && existsInModelsData && !selectedModelId) {
      dispatch(
        setSelectedModelId({
          modelId: savedModelId,
          projectId: selectedProjectId || '',
        })
      )
    } else if (!existsInModelsData) {
      localStorage.removeItem('selectedModelId')
      if (modelsData?.models?.length) {
        dispatch(
          setSelectedModelId({
            modelId: modelsData.models[0].id,
            projectId: selectedProjectId || '',
          })
        )
        localStorage.setItem('selectedModelId', modelsData.models[0].id)
      }
    }
  }, [dispatch, selectedModelId, modelsData, selectedProjectId])

  const [createExport] = useCreateExportMutation()
  const [deleteExport] = useDeleteExportMutation()

  const handleCreateExport = async (selectedTypes: ExportType[]): Promise<void> => {
    try {
      if (!project?.id || !project?.datasetid) {
        throw new Error('Project ID or Dataset ID is missing')
      }

      await createExport({
        name: `${project.name}-${new Date().toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}`,
        export_types: selectedTypes,
        project_id: project.id,
        dataset_id: project.datasetid,
        model_id: selectedModelId || undefined,
      })
    } catch (err) {
      setError(
        err
          ? (err as any).data?.message || 'Failed to create export'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    }
  }

  const handleDeleteExport = async (id: string): Promise<void> => {
    try {
      await deleteExport(id)
      setShowDeleteConfirm(false)
      setSelectedExport(null)
    } catch (err) {
      setError(
        err
          ? (err as any).data?.message || 'Failed to delete export'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    }
  }

  const initiateDelete = (export_: Export | undefined): void => {
    if (export_) {
      setSelectedExport(export_)
      setShowDeleteConfirm(true)
    }
  }

  const renderDownloadButton = (exportItem: Export): React.ReactNode => {
    // For single content key, render a simple ExportButton
    if (exportItem.content_keys.length === 1) {
      const filename = exportItem.content_keys[0].split('/').pop()
      return (
        <ExportButton
          exportId={exportItem.id}
          uri={exportItem.content_keys[0]}
          filename={filename}
          size="sm"
        >
          Download
        </ExportButton>
      )
    }

    // For multiple content keys, render a dropdown with ExportButtons
    return (
      <Dropdown>
        <Dropdown.Toggle variant="primary" size="sm">
          Download
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {exportItem.content_keys.map((key) => {
            const filename = key.split('/').pop()
            return (
              <Dropdown.Item key={key} className="p-2">
                <ExportButton
                  exportId={exportItem.id}
                  uri={key}
                  filename={filename}
                  showProgress={false}
                >
                  {filename}
                </ExportButton>
              </Dropdown.Item>
            )
          })}
        </Dropdown.Menu>
      </Dropdown>
    )
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center">
        <Spinner animation="border" />
      </div>
    )
  }

  if (apiError) {
    return <div>Error loading exports</div>
  }

  return (
    <>
      <StyledCard>
        <Card.Header className="d-flex justify-content-between align-items-center">
          {!isModal && <h4>Exports</h4>}
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={() => setShowExportTypeDialog(true)}>
              Create New Export
            </Button>
            {isModal && onClose && (
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <StyledTable hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>State</th>
                <th>Created</th>
                <th>Actions</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {exportsData?.exports?.map(
                (exportItem: Export) =>
                  showExportItem(exportItem) && (
                    <tr key={exportItem?.id}>
                      <td>
                        {exportItem?.name} - {exportItem?.model?.name}
                      </td>
                      <td>{exportItem?.types?.join(', ')}</td>
                      <td>
                        {exportItem?.state.toLowerCase() === 'running' ? (
                          <div className="d-flex align-items-center">
                            <Badge bg="warning">{exportItem?.state}</Badge>
                          </div>
                        ) : (
                          <Badge
                            bg={
                              exportItem?.state.toLowerCase() === 'complete' ? 'success' : 'warning'
                            }
                          >
                            {exportItem?.state}
                          </Badge>
                        )}
                      </td>
                      <td>{new Date(exportItem?.created_at || '').toLocaleDateString()}</td>
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => initiateDelete(exportItem)}
                        >
                          Delete
                        </Button>
                      </td>
                      <td>
                        {exportItem?.state.toLowerCase() === 'complete' &&
                        exportItem.content_keys?.length ? (
                          renderDownloadButton(exportItem)
                        ) : (
                          <Button variant="primary" size="sm" disabled className="w-100">
                            Download
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
              )}
            </tbody>
          </StyledTable>
        </Card.Body>
      </StyledCard>

      <ConfirmDialog
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirmRight={() => selectedExport && handleDeleteExport(selectedExport.id)}
        title="Delete Export"
        message={`Are you sure you want to delete export "${selectedExport?.name}"?`}
        confirmRightText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ExportTypeDialog
        show={showExportTypeDialog}
        onHide={() => setShowExportTypeDialog(false)}
        onConfirm={handleCreateExport}
      />

      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={error} />
    </>
  )
}

export default ExportsSubView
